import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { memo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import ReactModal from "react-modal";
import { useRecoilValue } from "recoil";

import Button from "components/Button";
import FilesField from "components/FilesField";
import SmartEditor from "components/SmartEditor";
import { useTransferQuestion } from "hooks/apis/question";
import { userState } from "store/store";
import { SmartEditorType } from "types/types";
import SimpleModal from "./SimpleModal";

type FormValues = {
  files: File[];
};

const TransferRequest = ({
  questionBNo,
  title,
}: {
  questionBNo: string;
  title: string;
}) => {
  const modal = useModal();
  const user = useRecoilValue(userState);
  const methods = useForm<FormValues>();
  const transferQuestionMutation = useTransferQuestion();
  const contentRef = useRef<SmartEditorType>();

  const closeModal = () => {
    modal.hide();
    modal.reject();
  };

  const onSubmit = (form: FormValues) => {
    const content = contentRef.current!.getContent() as string;
    if (!content || content === "<p><br></p>") {
      contentRef.current!.focus();
      return;
    }

    NiceModal.show(SimpleModal, {
      title: "이관 신청",
      message: "이관 신청 시 취소가 어렵습니다.\n이관 신청 하시겠습니까?",
      submitText: "이관",
    })
      .then(() => {
        transferQuestionMutation.mutate(
          { ...form, questionBNo, content },
          {
            onSuccess: () => {
              NiceModal.show(SimpleModal, {
                title: "이관 신청",
                message: "이관 신청이 완료되었습니다",
                showCancel: false,
              }).then(() => {
                modal.hide();
                modal.resolve();
              });
            },
          },
        );
      })
      .catch(() => {});
  };

  const onError = () => {
    const content = contentRef.current!.getContent() as string;
    if (!content || content === "<p><br></p>") {
      contentRef.current!.focus();
      return;
    }
    NiceModal.show(SimpleModal, {
      title: "이관 신청",
      message:
        "해당 질문과 관련된 강의/교재/문제 가 잘 보이도록 촬영하여 첨부해주시기 바랍니다.",
      showCancel: false,
    });
  };

  return (
    <ReactModal
      overlayClassName="overflow-auto flex fixed size-full top-0 left-0 z-30"
      className="bg-[rgba(0,0,0,0.2)] min-w-[1280px] min-h-[720px] flex justify-center items-center size-full outline-none"
      isOpen={modal.visible}
      onRequestClose={closeModal}
      onAfterClose={() => {
        modal.remove();
      }}
    >
      <FormProvider {...methods}>
        <main className="bg-white flex flex-col w-[60rem] p-[1.875rem] pt-10 rounded-md">
          <header className="space-y-[0.625rem] text-center">
            <h2 className="font-bold text-24 leading-[1.375rem]">이관 신청</h2>
            <h3 className="text-14">
              이관을 신청하는 이유를 상세히 작성해주세요
            </h3>
          </header>
          <form
            onSubmit={methods.handleSubmit(onSubmit, onError)}
            className="flex flex-col flex-1 overflow-hidden pt-[0.625rem]"
          >
            <table className="table-fixed w-full border border-gray">
              <tbody>
                <tr className="border-b border-gray">
                  <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                    제목
                  </th>
                  <td className="bg-white pl-[1.875rem] pr-5">{title}</td>
                </tr>
                <tr className="border-b border-gray">
                  <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                    이관자
                  </th>
                  <td className="bg-white pl-[1.875rem] pr-5">
                    {user?.name}({user?.userId})
                  </td>
                </tr>
                <tr className="border-b border-gray">
                  <th className="w-[7rem] align-top pt-[0.938rem] bg-lightGray px-6 text-left text-16 whitespace-nowrap">
                    이관 사유*
                  </th>
                  <td className="bg-white px-5 py-4">
                    <SmartEditor
                      ref={contentRef}
                      id="transfer"
                      defaultValue=""
                      autofocus
                    />
                  </td>
                </tr>
                <tr>
                  <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16 whitespace-nowrap">
                    첨부파일*
                  </th>
                  <td className="bg-white px-[1.875rem] pt-[0.625rem] pb-2">
                    <p className="text-12 text-red -mb-[0.313rem]">
                      * 해당 질문과 관련된{" "}
                      <strong className="text-bold">강의/교재/문제</strong> 가
                      잘 보이도록 촬영하여 첨부해주시기 바랍니다. (필수)
                    </p>
                    <FilesField required />
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="flex justify-end gap-[0.625rem] mt-[1.875rem]">
              <Button outline className="w-[10.688rem]" onClick={closeModal}>
                취소
              </Button>
              <Button type="submit" className="w-[10.688rem]">
                이관신청
              </Button>
            </div>
          </form>
        </main>
      </FormProvider>
    </ReactModal>
  );
};

const TransferRequestModal = NiceModal.create(memo(TransferRequest));
TransferRequestModal.displayName = "TransferRequestModal";

export default TransferRequestModal;
